<template>
	<div
		class="tile-container"
		:disabled="disabled"
		:selected="selected"
		:type="type"
		@click="handleClick($event)"
	>
		<div v-if="$slots['action']" class="tile-action">
			<slot name="action" />
		</div>
		<div class="tile-content">
			<slot />

			<h5 v-if="title" class="tile-title">
				{{ title }}
			</h5>
			<p v-if="description" class="tile-description paragraph-2">
				{{ description }}
			</p>
		</div>
		<div v-if="$slots['footer']" class="tile-footer" :type="footerType">
			<slot name="footer" />
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";
/**
 * Tile is a container that visually represents important entities such as projects, sources etc. <br/>
 * NOTE : Make sure you wraps all Tiles in ".tile-grid" element . E.g. <code>&lt;div class="tile-grid"&gt;...&lt;/div&gt;</code>
 */
export default defineComponent({
	name: "Tile",
	props: {
		/**
		 * true | false
		 */
		disabled: {
			type: Boolean,
			default: false
		},
		/**
		 *  'success' | 'error' | 'warning' | 'default'
		 */
		type: {
			type: String,
			default: "default",
			validator(value) {
				return ["success", "error", "warning", "default"].includes(value);
			}
		},
		/**
		 * 'indication' | 'action'
		 */
		footerType: {
			type: String,
			default: "indication",
			validator(value) {
				return ["indication", "action"].includes(value);
			}
		},
		/**
		 * Title of tile
		 */
		title: {
			type: String,
			default: null
		},
		/**
		 * Description of tile
		 */
		description: {
			type: String,
			default: null
		},
		/**
		 * true | false
		 */
		selected: {
			type: Boolean,
			default: false
		}
	},
	methods: {
		handleClick(event) {
			/**
			 * Click event.
			 *
			 * @event click
			 */
			if (!this.disabled) {
				this.$emit("click", event);
			}
		}
	}
});
</script>
<style lang="scss">
@mixin tile(
	$background-color,
	$border-color,
	$hover,
	$focus,
	$font-color,
	$tile-type-backgrounds,
	$selected-color,
	$selected-background-color
) {
	.tile-container {
		border: 1px solid $border-color;
		background-color: $background-color !important;
		cursor: pointer;
		@each $type, $color in $tile-type-backgrounds {
			&[type="#{$type}"] {
				background: $color;
			}
		}
		> .tile-content {
			> .tile-title {
				color: $font-color;
			}
			> .tile-description {
				color: $font-color;
			}
		}
		> .tile-footer {
			border-top: 1px solid $border-color;
		}

		&[selected] {
			border: 1px solid $selected-color;
			//	background: $selected-background-color 0% 0% no-repeat padding-box !important;
			> .tile-footer {
				border-top: 1px solid $selected-color;
			}
			&:after {
				background-color: $selected-background-color;
			}
		}
		&:not([disabled]) {
			&:active,
			&:hover:not(:active) {
				&:before {
					background-color: $hover;
				}
			}
			&:focus {
				border: 1px solid $focus;
				> .tile-footer {
					border-top: 1px solid $focus;
				}
			}
		}
	}
}

.tile-container {
	display: inline-flex;
	flex-direction: column;
	border-radius: 12px;
	position: relative;
	// max-width: 252px;

	> .tile-content {
		// min-height: 150px;
		// min-width: 200px;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		align-items: center;
		padding-top: 28px;
		padding-bottom: 28px;

		> .tile-title {
			margin-top: 12px;
			max-width: 80%;
			text-align: center;
			line-height: 12px;
			word-break: break-word;
		}

		> .tile-description {
			// margin-bottom: 24px;
			margin-top: 8px;
			max-width: 80%;
			text-align: center;
			line-height: 18px;
			font-weight: lighter;
			word-break: break-word;
		}
	}

	> .tile-action {
		position: absolute;
		right: 12px;
		top: 12px;
	}

	> .tile-footer {
		max-height: 36px;
		padding: 12px;
		display: flex;
		align-items: center;
		margin-top: auto;
		gap: 6px;

		&[type="indication"] {
			justify-content: center;
		}
		&[type="action"] {
			justify-content: space-between;
		}

		> .icon-container > .icon {
			padding: 0px;
		}
	}

	&[disabled] {
		cursor: not-allowed !important;
		filter: grayscale(100%);
	}
	&:not([disabled]) {
		&:active,
		&:hover:not(:active) {
			&:before {
				content: "";
				position: absolute;
				top: 0;
				right: 0;
				bottom: 0;
				left: 0;
				border-radius: 12px;
			}
		}

		&:active {
			&:before {
				opacity: 0.08;
				cursor: pointer;
			}
		}

		&:hover:not(:active) {
			&:before {
				opacity: 0.04;
			}
		}

		&:focus {
			outline: none;
		}
	}

	&:after {
		position: absolute;
		top: 0px;
		left: 0px;
		bottom: 0px;
		right: 0px;
		opacity: 1;
		transition: opacity 0.3s linear;
	}

	&[selected] {
		position: relative;
		&:after {
			content: "";
			opacity: 0.2;
		}
	}
}

.tile-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	grid-gap: 24px;
	grid-auto-rows: 1fr;
}

$slideout-tile-type-backgrounds: (
	"success": var(--dashboard-success-gradient),
	"error": var(--dashboard-danger-gradient),
	"warning": var(--dashboard-warning-gradient)
);
.sideout-container {
	@include tile(
		var(--slideout-element-light),
		var(--slideout-border-light),
		var(--slideout-element-dark),
		var(--slideout-primary),
		var(--slideout-font-dark),
		$slideout-tile-type-backgrounds,
		var(--slideout-success),
		var(--slideout-success-light)
	);
}
$dashboard-tile-type-backgrounds: (
	"success": var(--dashboard-success-gradient),
	"error": var(--dashboard-danger-gradient),
	"warning": var(--dashboard-warning-gradient)
);
@include tile(
	var(--dashboard-element-light),
	var(--dashboard-border-light),
	var(--dashboard-element-dark),
	var(--dashboard-primary),
	var(--dashboard-font-dark),
	$dashboard-tile-type-backgrounds,
	var(--dashboard-success),
	var(--dashboard-success-light)
);
</style>
